import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer } from 'preact-i18n';
import { route } from 'preact-router';

import localUtil from '../../lib/util';
import { appAlert } from '../../lib/dialogs';

const OPTION_LABELS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'];

@observer
class Poll extends Component {
    constructor(props) {
        super(props);
        this.state = {
            hasVoted: false,
            userVoteIndex: null,
            isVoting: false,
            localVotes: null,
            showVoters: false,
            voterDetails: null,
            loadingVoters: false,
        };
    }

    loadAll = (props = this.props) => {
        const { poll } = props;
        const { userStore } = this.props.stores;
        const { user } = userStore;

        if (!poll?.options?.length) return;

        const votes = poll.votes || [];
        const userVote = votes.find(v => v.user === user.id);
        if (userVote) {
            this.setState({
                hasVoted: true,
                userVoteIndex: userVote.optionIndex,
            });
        } else {
            this.setState({
                hasVoted: false,
                userVoteIndex: null,
            });
        }
    }

    vote = async (e) => {
        e.stopPropagation();
        e.preventDefault();

        const { userStore, pollStore } = this.props.stores;
        const { user } = userStore;

        if (!user.id) {
            route('/login');
            return;
        }

        const { optionindex } = e.target.closest('button').dataset;
        const optionIndex = parseInt(optionindex, 10);
        const { poll, onVoteComplete } = this.props;

        this.setState({ isVoting: true });

        try {
            const response = await pollStore.votePoll(poll.id, optionIndex);
            if (response.status === 200) {
                // Update local state immediately for responsive UI
                const currentVotes = poll.votes || [];
                const newVote = {
                    id: response.data.voteId,
                    user: user.id,
                    optionIndex,
                    createdDate: new Date().toISOString(),
                };
                this.setState({
                    hasVoted: true,
                    userVoteIndex: optionIndex,
                    isVoting: false,
                    localVotes: [...currentVotes, newVote],
                });

                // Trigger parent refresh if available
                if (onVoteComplete) {
                    onVoteComplete();
                }
            } else {
                this.setState({ isVoting: false });
                (await appAlert(response.message || 'Failed to vote'));
            }
        } catch (err) {
            this.setState({ isVoting: false });
            (await appAlert('Failed to vote. Please try again.'));
        }
    }

    calculateResults = () => {
        const { poll } = this.props;
        const { localVotes } = this.state;
        const votes = localVotes || poll?.votes || [];

        if (!poll?.options?.length) return [];

        const totalVotes = votes.length;
        return poll.options.map((option, idx) => {
            const voteCount = votes.filter(v => v.optionIndex === idx).length;
            const percentage = totalVotes > 0 ? Math.round((voteCount / totalVotes) * 100) : 0;
            return {
                label: OPTION_LABELS[idx],
                text: option,
                voteCount,
                percentage,
            };
        });
    }

    toggleVoters = async () => {
        const { showVoters, voterDetails } = this.state;
        if (showVoters) {
            this.setState({ showVoters: false });
            return;
        }
        if (voterDetails) {
            this.setState({ showVoters: true });
            return;
        }
        const { pollStore } = this.props.stores;
        const { poll } = this.props;
        this.setState({ loadingVoters: true });
        try {
            const response = await pollStore.getPollResults(poll.id);
            if (response.status === 200) {
                this.setState({
                    voterDetails: response.data,
                    showVoters: true,
                    loadingVoters: false,
                });
            } else {
                this.setState({ loadingVoters: false });
            }
        } catch (err) {
            this.setState({ loadingVoters: false });
        }
    }

    isPollExpired = () => {
        const { poll } = this.props;
        if (!poll?.expireDate) return false;
        return new Date(poll.expireDate) < new Date();
    }

    componentDidMount() {
        this.loadAll();
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.poll !== this.props.poll) {
            this.setState({ localVotes: null });
            this.loadAll(nextProps);
        }
    }

    render() {
        const { hasVoted, userVoteIndex, isVoting, localVotes } = this.state;
        const { poll } = this.props;
        const { userStore } = this.props.stores;
        const { user, isAdmin } = userStore;
        const isLoggedIn = !!user.id;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');

        if (!poll?.options?.length) return null;

        const results = this.calculateResults();
        const votes = localVotes || poll.votes || [];
        const totalVotes = votes.length;
        const isExpired = this.isPollExpired();
        const showResults = hasVoted || isExpired;

        return (
            <div
                class='w-100 d-flex flex-column mt-5 mb-2 p-3 rounded-3 border'
                style={`background-color: ${darkmode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'};`}
            >
                {/* Poll Question */}
                {poll.question && (
                    <div class='fw-bold mb-3' style='font-size: 1.1em; line-height: 1.3;'>
                        {poll.question}
                    </div>
                )}

                {/* Poll Options */}
                <div class='d-flex flex-column gap-2'>
                    {results.map((option, idx) => (
                        <div key={idx} class='position-relative'>
                            {showResults ? (
                                // Results view (after voting or expired)
                                <div
                                    class={`d-flex flex-row align-items-center rounded-3 overflow-hidden position-relative ${userVoteIndex === idx ? 'border border-2 border-primary' : 'border'} ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}
                                    style='min-height: 50px;'
                                >
                                    {/* Progress bar background */}
                                    <div
                                        class='position-absolute h-100'
                                        style={`
                                            left: 0; top: 0;
                                            width: ${option.percentage}%;
                                            background-color: ${userVoteIndex === idx ? 'rgba(var(--bs-primary-rgb), 0.3)' : 'rgba(var(--bs-primary-rgb), 0.15)'};
                                            transition: width 0.5s ease;
                                        `}
                                    />
                                    {/* Option label */}
                                    <div
                                        class={`d-flex justify-content-center align-items-center fw-bold ms-2 rounded-3 ${userVoteIndex === idx ? 'bg-primary text-white' : (darkmode ? 'bg-secondary text-white' : 'bg-white')}`}
                                        style='width: 32px; height: 32px; z-index: 1;'
                                    >
                                        {option.label}
                                    </div>
                                    {/* Option text */}
                                    <div class='flex-fill px-3 py-2' style='z-index: 1;'>
                                        {option.text}
                                    </div>
                                    {/* Percentage */}
                                    <div class={`px-3 fw-bold ${userVoteIndex === idx ? 'text-primary' : ''}`} style='z-index: 1;'>
                                        {option.percentage}%
                                    </div>
                                </div>
                            ) : (
                                // Voting view
                                <button
                                    type='button'
                                    class={`btn w-100 d-flex flex-row align-items-center text-start rounded-3 border ${darkmode ? 'bg-darkmode text-white' : 'bg-lightmode'}`}
                                    onClick={this.vote}
                                    data-optionindex={idx}
                                    disabled={!isLoggedIn || isVoting}
                                    style='min-height: 50px;'
                                >
                                    {/* Option label */}
                                    <div
                                        class={`d-flex justify-content-center align-items-center fw-bold rounded-3 ${darkmode ? 'bg-secondary text-white' : 'bg-white'}`}
                                        style='width: 32px; height: 32px;'
                                    >
                                        {option.label}
                                    </div>
                                    {/* Option text */}
                                    <div class='flex-fill px-3'>
                                        {option.text}
                                    </div>
                                </button>
                            )}
                        </div>
                    ))}
                </div>

                {/* Footer info */}
                <div class='d-flex flex-row justify-content-between align-items-center mt-3 text-muted'>
                    <small>
                        {totalVotes} <Text id='poll.votes'>stemmer</Text>
                    </small>
                    {isExpired ? (
                        <small class='text-danger'>
                            <i class='fa-solid fa-clock me-1' />
                            <Text id='poll.expired'>Avstemning avsluttet</Text>
                        </small>
                    ) : poll.expireDate ? (
                        <small>
                            <Text id='poll.expires'>Avsluttes</Text> {localUtil.formatDate(poll.expireDate)}
                        </small>
                    ) : null}
                </div>

                {/* Login prompt for non-logged in users */}
                {!isLoggedIn && !showResults && (
                    <div class='text-center mt-2'>
                        <button
                            type='button'
                            class='btn btn-link btn-sm'
                            onClick={() => route('/login')}
                        >
                            <Text id='poll.login-to-vote'>Logg inn for a stemme</Text>
                        </button>
                    </div>
                )}

                {/* Admin info */}
                {isAdmin && (
                    <div class='mt-2'>
                        <button
                            type='button'
                            class='btn btn-link btn-sm p-0 text-body-secondary text-decoration-none'
                            onClick={this.toggleVoters}
                            disabled={this.state.loadingVoters}
                        >
                            <small>
                                <i class={`fa-solid ${this.state.showVoters ? 'fa-eye-slash' : 'fa-eye'} me-1`} />
                                {this.state.loadingVoters ? (
                                    <span><i class='fa-solid fa-spinner fa-spin me-1' /><Text id='poll.loading-voters'>Laster...</Text></span>
                                ) : this.state.showVoters ? (
                                    <Text id='poll.hide-voters'>Skjul hvem som har stemt</Text>
                                ) : (
                                    <Text id='poll.admin-can-see-voters'>Admin kan se hvem som har stemt</Text>
                                )}
                            </small>
                        </button>
                        {this.state.showVoters && this.state.voterDetails && (
                            <div class='mt-2 p-2 rounded-3 border bg-body-secondary'>
                                {this.state.voterDetails.voteCounts.map((vc, idx) => (
                                    <div key={idx} class='mb-2'>
                                        <div class='fw-bold mb-1'>
                                            <span class='badge bg-primary me-1'>{OPTION_LABELS[vc.optionIndex]}</span>
                                            {vc.optionText} ({vc.count})
                                        </div>
                                        {this.state.voterDetails.votes
                                            .filter(v => v.optionIndex === vc.optionIndex)
                                            .map((v, vIdx) => (
                                                <div key={vIdx} class='ms-3 small text-body-secondary'>
                                                    <i class='fa-solid fa-user me-1' />
                                                    {v.userDetails.firstname} {v.userDetails.lastname}
                                                    {v.userDetails.email && <span class='ms-1'>({v.userDetails.email})</span>}
                                                </div>
                                            ))
                                        }
                                        {this.state.voterDetails.votes.filter(v => v.optionIndex === vc.optionIndex).length === 0 && (
                                            <div class='ms-3 small text-body-secondary fst-italic'>
                                                <Text id='poll.no-votes-option'>Ingen stemmer</Text>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }
}

export default Poll;
